import type { Story } from '@/types/cmsTypes';
import type { ProjectInstance } from '@/types/projectTypes';
import { safeParseJson } from '@/utils/safeParseJson';

export interface ProjectFormFields {
  title: string;
  creatorName: string;
  description: string;
  duration: string;
  tags: string;
  lat: string;
  lng: string;
  address: string;
  city: string;
}

export const FORM_FIELDS = ['title', 'creatorName', 'description', 'duration', 'tags', 'lat', 'lng', 'address', 'city'] as const;

export const storyHasLocation = (story: Story) =>
  Boolean(story.locationInfo?.address || (story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng));

export function initialFormFields(project: ProjectInstance, stories: Story[]): ProjectFormFields {
  const parsedTags = Array.isArray(project.tags) ? project.tags : safeParseJson<string[]>(project.tags as any) || [];
  const fields = {
    title: project.projectName || '',
    creatorName: project.sourceName || '',
    description: project.description || '',
    duration: project.totalDuration ? Math.round(project.totalDuration / 60).toString() : '',
    tags: parsedTags.join(', '),
  };
  const story = stories.find(storyHasLocation);
  if (story?.locationInfo) {
    return {
      ...fields,
      lat: story.locationInfo.coordinates?.lat?.toString() || '',
      lng: story.locationInfo.coordinates?.lng?.toString() || '',
      address: story.locationInfo.address || '',
      city: story.locationInfo.city || '',
    };
  }
  const coords = project.locationInfo?.coordinates;
  return {
    ...fields,
    lat: coords?.lat === 0 ? '' : (coords?.lat?.toString() || ''),
    lng: coords?.lng === 0 ? '' : (coords?.lng?.toString() || ''),
    address: project.locationInfo?.address || '',
    city: project.locationInfo?.city || '',
  };
}

// The save asks for both coordinates rather than storing a 0
export const coordinatesIncomplete = ({ lat, lng }: Pick<ProjectFormFields, 'lat' | 'lng'>) =>
  Boolean(lat.trim()) !== Boolean(lng.trim());

export function projectLocationJson({ lat, lng, address, city }: Pick<ProjectFormFields, 'lat' | 'lng' | 'address' | 'city'>): string | null {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);
  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);
  if (!hasCoords && !address) return null;
  return JSON.stringify({
    address: address || '',
    city: city || null,
    ...(hasCoords ? { coordinates: { lat: latitude, lng: longitude } } : {}),
  });
}
